import React, {useState} from 'react';
import Modal from "../../../../Common/Modal/Modal";
import ModalContent from "../../../../Common/Modal/ModalContent/ModalContent";
import {withTranslation} from "react-i18next";
import PropTypes from "prop-types";
import styles from '../../../SearchWidget.module.scss';
import imgPath from '../../../../../assets/media/WorldMap.svg';
import WindowEventWatcher from "../../../../Common/WindowEventWatcher";

const AirportsSelectorPopup = ({allowKeyboard, selectAirportHandler, open, airports, t, ...props}) => {

    if (!open) {
        return null;
    }

    const createRegionFromAirports = (airports = []) => {
        if (!Array.isArray(airports) || (Array.isArray(airports) && !airports.length)) {
            return airports;
        }

        return {name: airports[0].regionName, airports: [...airports]};
    };

    const orderRegions = regions => {
        const orderedRegions = {};
        if (regions.hasOwnProperty('Azores')) orderedRegions['Azores'] = regions['Azores'];
        if (regions.hasOwnProperty('Açores')) orderedRegions['Açores'] = regions['Açores'];
        if (regions.hasOwnProperty('Portugal Mainland')) orderedRegions['Portugal Mainland'] = regions['Portugal Mainland'];
        if (regions.hasOwnProperty('Portugal Continental')) orderedRegions['Portugal Continental'] = regions['Portugal Continental'];
        if (regions.hasOwnProperty('Madeira')) orderedRegions['Madeira'] = regions['Madeira'];
        if (regions.hasOwnProperty('Madeira')) orderedRegions['Madeira'] = regions['Madeira'];
        if (regions.hasOwnProperty('United States')) orderedRegions['United States'] = regions['United States'];
        if (regions.hasOwnProperty('Estados Unidos')) orderedRegions['Estados Unidos'] = regions['Estados Unidos'];
        if (regions.hasOwnProperty('Canada')) orderedRegions['Canada'] = regions['Canada'];
        if (regions.hasOwnProperty('Canadá')) orderedRegions['Canadá'] = regions['Canadá'];
        if (regions.hasOwnProperty('Europe')) orderedRegions['Europe'] = regions['Europe'];
        if (regions.hasOwnProperty('Europa')) orderedRegions['Europa'] = regions['Europa'];
        if (regions.hasOwnProperty('Africa')) orderedRegions['Africa'] = regions['Africa'];
        if (regions.hasOwnProperty('África')) orderedRegions['África'] = regions['África'];

        return orderedRegions;
    };

    const distributeRegions = airports => {
        if (!airports.length) {
            return {selected: null, airports: [], regions: null};
        }

        const regions = {};
        for (const airport of airports) {
            if (Array.isArray(regions[airport.regionName])) {
                regions[airport.regionName].push({...airport});
            } else {
                regions[airport.regionName] = [{...airport}];
            }
        }

        const orderedRegions = orderRegions(regions);
        const firstRegionName = Object.keys(orderedRegions)[0];
        return {selected: createRegionFromAirports([...orderedRegions[firstRegionName]]), regions: {...orderedRegions}};
    };

    const airportsDistribution = distributeRegions(airports);
    const regions = airportsDistribution.regions;
    const [regionSelected, setRegionSelected] = useState(airportsDistribution.selected);
    const [airportAnchorIdx, setAirportAnchor] = useState(allowKeyboard ? 0 : -1);

    const previousRegion = () => {
        if (!regionSelected) {
            return null;
        }

        const regionKeys = Object.keys(regions);
        for (let i = 0; i < regionKeys.length; i++) {
            if (regionKeys[i] === regionSelected.name) {
                if (i > 0) {
                    return createRegionFromAirports(regions[regionKeys[i - 1]]);
                }

                return createRegionFromAirports(regions[regionKeys[regionKeys.length - 1]]);
            }
        }

        return null;
    };

    const nextRegion = () => {
        const regionKeys = Object.keys(regions);

        if (!regionSelected) {
            return regionKeys.length ? regions[regionKeys[0]] : null;
        }

        for (let i = 0; i < regionKeys.length; i++) {
            if (regionKeys[i] === regionSelected.name) {
                if (i < (regionKeys.length - 1)) {
                    return createRegionFromAirports(regions[regionKeys[i + 1]]);
                }

                return createRegionFromAirports(regions[regionKeys[0]]);
            }
        }

        return null;
    };

    const previousAirportIdx = () => {
        if (!regionSelected) {
            return 0;
        }

        const nextIdx = airportAnchorIdx - 1;
        if (nextIdx >= 0) {
            return nextIdx;
        }

        return regionSelected.airports.length - 1;
    };

    const nextAirportIdx = () => {
        if (!regionSelected) {
            return 0;
        }

        const nextIdx = airportAnchorIdx + 1;
        if (nextIdx < regionSelected.airports.length) {
            return nextIdx;
        }

        return 0;
    };

    const regionSelectHandler = regionSelected => {
        setAirportAnchor(allowKeyboard ? 0 : -1);
        setRegionSelected(regionSelected);
    };

    const renderRegions = () => {

        if (!regions) {
            return null;
        }

        return Object.keys(regions).map(regionName => {
            const region = {name: regionName, airports: [...regions[regionName]]};
            const regionClasses = [styles.ASPRegion];
            const isSelected = regionSelected ? regionSelected.name === regionName : false;
            if (isSelected) regionClasses.push(styles.ASPRegionSelected);
            return (
                <div key={regionName}
                     onClick={() => regionSelectHandler(region)}
                     className={regionClasses.join(' ')}>
                    {regionName}
                </div>
            );
        });
    };

    const renderAirports = () => {
        if (!regionSelected || !regionSelected.hasOwnProperty('airports')) {
            return null;
        }

        return regionSelected.airports.map((airport, idx) => {
            const airportClasses = [styles.ASPAirportContainer];
            if (airportAnchorIdx === idx) airportClasses.push(styles.ASPAirportContainerSelected);

            return (
                <div key={idx} className={airportClasses.join(' ')} onClick={() => selectAirportHandler(airport)}>
                    <div className={styles.ASPAirportLeftContent}>{airport.code}</div>
                    <div className={styles.ASPAirportRightContent}>{airport.name}, {airport.cityName}</div>
                </div>
            );
        });
    };

    const keyDownHandler = e => {
        if (!e) {
            return;
        }

        const code = e.code ? e.code : e.key;

        switch (code) {
            case 'Up':
            case 'ArrowUp':
                setAirportAnchor(previousAirportIdx());
                break;
            case 'Down':
            case 'ArrowDown':
                setAirportAnchor(nextAirportIdx());
                break;
            case 'Left':
            case 'ArrowLeft':
                setAirportAnchor(0);
                setRegionSelected(previousRegion());
                break;
            case 'Right':
            case 'ArrowRight':
                setAirportAnchor(0);
                setRegionSelected(nextRegion());
                break;
            case ' ':
            case 'Space':
            case 'NumpadEnter':
            case 'Enter':
                if (!regionSelected) {
                    return;
                }

                selectAirportHandler(regionSelected.airports[airportAnchorIdx]);
                break;
            default:
                return;
        }

        e.stopPropagation();
        e.preventDefault();
    };

    return (
        <Modal open={open} {...props}>
            <ModalContent>
                <WindowEventWatcher listening={open && allowKeyboard} eventName={'keydown'} handlerFn={keyDownHandler}/>
                <div className={styles.ASPContainer}>
                    <div className={styles.ASPHeaderContainer}>
                        <div className={styles.ASPHeaderLeft}>
                            <img alt='worldMap' className={styles.ASPHeaderLeftImg} src={imgPath}/>
                        </div>
                        <div className={styles.ASPHeaderRight}>{t('Search Airport')}</div>
                    </div>
                    <div className={styles.ASPRegionsContainer}>
                        {renderRegions()}
                    </div>
                    <div className={styles.ASPAirportsContainer}>
                        {renderAirports()}
                    </div>
                </div>
            </ModalContent>
        </Modal>
    );
};

AirportsSelectorPopup.propTypes = {
    ...Modal.propTypes,
    allowKeyboard: PropTypes.bool,
    selectAirportHandler: PropTypes.func,
    airports: PropTypes.array,
    t: PropTypes.func,
};

AirportsSelectorPopup.defaultProps = {
    ...Modal.defaultProps,
    allowKeyboard: false,
    selectAirportHandler: () => null,
    airports: [],
    t: str => str
};

export default withTranslation()(AirportsSelectorPopup);
